import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import GlobalLayout from './components/layout/GlobalLayout'
import ProtectedRoute from './components/ProtectedRoute'
import PageWrapper from './components/PageWrapper'
import LoadingState from './components/ui/LoadingState'
import { usePermissions } from './hooks/usePermissions'

const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'))
const DashboardHome = lazy(() => import('./pages/DashboardHome'))
const Usuarios = lazy(() => import('./pages/Usuarios'))
const Mascotas = lazy(() => import('./pages/Mascotas'))
const MascotasMobile = lazy(() => import('./pages/mobile/MascotasMobile'))
const MascotaDetailMobile = lazy(() => import('./pages/mobile/MascotaDetailMobileExact'))
const MascotaDetailDesktop = lazy(() => import('./pages/MascotaDetailDesktop'))
const Citas = lazy(() => import('./pages/Citas'))
const CitasMobile = lazy(() => import('./pages/mobile/CitasMobile'))
const CitaDetailMobile = lazy(() => import('./pages/mobile/CitaDetailMobile'))
const Fichas = lazy(() => import('./pages/Fichas'))
const FichasMobile = lazy(() => import('./pages/mobile/FichasMobile'))
const FichaRapidaMobile = lazy(() => import('./pages/mobile/FichaRapidaMobile'))
const FichaDetailMobile = lazy(() => import('./pages/mobile/FichaDetailMobile'))
const FichaDetailDesktop = lazy(() => import('./pages/FichaDetailDesktop'))
const Veterinarios = lazy(() => import('./pages/Veterinarios'))
const Reportes = lazy(() => import('./pages/Reportes'))
const Notificaciones = lazy(() => import('./pages/Notificaciones'))
const Configuracion = lazy(() => import('./pages/Configuracion'))
const SuscripcionesPendientes = lazy(() => import('./pages/SuscripcionesPendientes'))
const Perfil = lazy(() => import('./pages/Perfil'))
const NotificacionesMobile = lazy(() => import('./pages/mobile/NotificacionesMobileExact'))
const PerfilMobile = lazy(() => import('./pages/mobile/PerfilMobileExact'))
const PerfilEditMobile = lazy(() => import('./pages/mobile/PerfilEditMobileExact'))
const Clinicas = lazy(() => import('./pages/Clinicas'))
const ClinicasMobile = lazy(() => import('./pages/mobile/ClinicasMobileVectorExact'))
const ClinicaDetailMobile = lazy(() => import('./pages/mobile/ClinicaDetailMobileExact'))
const SuscripcionesClinicas = lazy(() => import('./pages/SuscripcionesClinicas'))
const AccesoMascotas = lazy(() => import('./pages/AccesoMascotas'))
const EditarMascota = lazy(() => import('./pages/EditarMascota'))
const AgendaHoy = lazy(() => import('./pages/AgendaHoy'))
const PlantillasConsulta = lazy(() => import('./pages/PlantillasConsulta'))
const OperacionesClinica = lazy(() => import('./pages/OperacionesClinica'))

function RouteFallback() {
  return <LoadingState message="Cargando página..." size="lg" />
}

function App() {
  const { isVeterinarioAdmin } = usePermissions()

  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        <Route
          path="/"
          element={
            <ProtectedRoute>
              <GlobalLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="dashboard" element={<DashboardHome />} />
          <Route
            path="usuarios"
            element={
              <ProtectedRoute allowedRoles={['SUPERADMIN']}>
                <Usuarios />
              </ProtectedRoute>
            }
          />
          <Route
            path="mascotas"
            element={
              <PageWrapper
                MobileComponent={MascotasMobile}
                DesktopComponent={Mascotas}
              />
            }
          />
          <Route
            path="mascotas/:id"
            element={
              <PageWrapper
                MobileComponent={MascotaDetailMobile}
                DesktopComponent={MascotaDetailDesktop}
              />
            }
          />
          <Route
            path="mascotas/:id/edit"
            element={
              <PageWrapper
                MobileComponent={EditarMascota}
                DesktopComponent={EditarMascota}
              />
            }
          />
          <Route
            path="citas"
            element={
              <PageWrapper
                MobileComponent={CitasMobile}
                DesktopComponent={Citas}
              />
            }
          />
          <Route
            path="citas/:id"
            element={
              <PageWrapper
                MobileComponent={CitaDetailMobile}
                DesktopComponent={CitaDetailMobile}
              />
            }
          />
          <Route
            path="fichas"
            element={
              <PageWrapper
                MobileComponent={FichasMobile}
                DesktopComponent={Fichas}
              />
            }
          />
          <Route
            path="fichas/nueva"
            element={
              <PageWrapper
                MobileComponent={FichaRapidaMobile}
                DesktopComponent={FichaRapidaMobile}
              />
            }
          />
          <Route
            path="fichas/:id"
            element={
              <PageWrapper
                MobileComponent={FichaDetailMobile}
                DesktopComponent={FichaDetailDesktop}
              />
            }
          />
          <Route
            path="clinicas"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPERADMIN', 'VETERINARIO']}>
                <PageWrapper
                  MobileComponent={ClinicasMobile}
                  DesktopComponent={Clinicas}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="veterinarios"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPERADMIN', 'VETERINARIO']}>
                <Veterinarios />
              </ProtectedRoute>
            }
          />
          <Route
            path="reportes"
            element={
              <ProtectedRoute allowedRoles={['ADMIN', 'SUPERADMIN', 'VETERINARIO']}>
                <Reportes />
              </ProtectedRoute>
            }
          />
          <Route
            path="notificaciones"
            element={
              <PageWrapper
                MobileComponent={NotificacionesMobile}
                DesktopComponent={Notificaciones}
              />
            }
          />
          <Route
            path="perfil"
            element={
              <PageWrapper
                MobileComponent={PerfilMobile}
                DesktopComponent={Perfil}
              />
            }
          />
          <Route
            path="perfil/editar"
            element={
              <PageWrapper
                MobileComponent={PerfilEditMobile}
                DesktopComponent={Perfil}
              />
            }
          />
          <Route
            path="suscripciones-clinicas"
            element={
              <ProtectedRoute allowedRoles={['DUEÑO']}>
                <PageWrapper
                  MobileComponent={ClinicasMobile}
                  DesktopComponent={SuscripcionesClinicas}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="suscripciones-clinicas/:id"
            element={
              <ProtectedRoute allowedRoles={['DUEÑO']}>
                <PageWrapper
                  MobileComponent={ClinicaDetailMobile}
                  DesktopComponent={ClinicaDetailMobile}
                />
              </ProtectedRoute>
            }
          />
          <Route
            path="acceso-mascotas"
            element={
              <ProtectedRoute allowedRoles={['VETERINARIO', 'PET_SITTER', 'ADMIN', 'SUPERADMIN']}>
                <AccesoMascotas />
              </ProtectedRoute>
            }
          />
          <Route
            path="agenda"
            element={
              <ProtectedRoute allowedRoles={['VETERINARIO', 'ADMIN', 'SUPERADMIN']}>
                <AgendaHoy />
              </ProtectedRoute>
            }
          />
          <Route
            path="plantillas-consulta"
            element={
              <ProtectedRoute allowedRoles={['VETERINARIO', 'ADMIN', 'SUPERADMIN']}>
                <PlantillasConsulta />
              </ProtectedRoute>
            }
          />
          <Route
            path="operaciones-clinica"
            element={
              <ProtectedRoute allowedRoles={['VETERINARIO', 'ADMIN', 'SUPERADMIN']}>
                <OperacionesClinica />
              </ProtectedRoute>
            }
          />
          <Route
            path="suscripciones-pendientes"
            element={
              <ProtectedRoute allowedRoles={['VETERINARIO']}>
                {isVeterinarioAdmin ? <SuscripcionesPendientes /> : <Navigate to="/" replace />}
              </ProtectedRoute>
            }
          />
          <Route path="configuracion">
            <Route
              path="roles"
              element={
                <ProtectedRoute allowedRoles={['SUPERADMIN']}>
                  <Configuracion />
                </ProtectedRoute>
              }
            />
            <Route
              path="condiciones-salud"
              element={
                <ProtectedRoute allowedRoles={['SUPERADMIN']}>
                  <Configuracion />
                </ProtectedRoute>
              }
            />
            <Route
              path="push-condicion-salud"
              element={
                <ProtectedRoute allowedRoles={['SUPERADMIN']}>
                  <Configuracion />
                </ProtectedRoute>
              }
            />
            <Route
              path="seo"
              element={
                <ProtectedRoute allowedRoles={['SUPERADMIN']}>
                  <Configuracion />
                </ProtectedRoute>
              }
            />
            <Route
              path="paises"
              element={
                <ProtectedRoute allowedRoles={['SUPERADMIN']}>
                  <Configuracion />
                </ProtectedRoute>
              }
            />
            <Route
              path="sistema"
              element={
                <ProtectedRoute allowedRoles={['SUPERADMIN']}>
                  <Configuracion />
                </ProtectedRoute>
              }
            />
            <Route
              index
              element={
                <ProtectedRoute allowedRoles={['SUPERADMIN']}>
                  <Configuracion />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default App
